"use client";
import React, { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import { decodeContact } from "@/lib/contact";
import { toTelHref, toWhatsAppNumber } from "@/lib/phone";
import styles from "./styles.module.scss";

// ─── Fallbacks, consistent with Footer / SimpleMenu ───
const DEFAULT_PHONE = "+41 41 914 10 60";
const DEFAULT_WHATSAPP = "+41 79 601 58 74";
const DEFAULT_EMAIL = "info@mondial.ch";

// ─── Inline SVG icons ───

const WhatsAppIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.36-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="#25D366"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16.001 16.001 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" fill="#1738b3"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" fill="#D3D3D3"/>
    <polyline points="22,6 12,13 2,6" fill="none" stroke="#666" strokeWidth="1.5"/>
  </svg>
);

// ─── Chat bubble icon ───
const ChatBubbleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.fabIcon}
  >
    <path
      d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
      fill="currentColor"
    />
  </svg>
);

export interface ContactFabButtonProps {
  siteConfig?: {
    phone?: string | null;
    whatsapp?: string | null;
    email?: string | null;
  } | null;
}

export const ContactFabButton = ({ siteConfig }: ContactFabButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("common");

  // Values arrive encoded from the server layout; decoded only on interaction.
  const phone = decodeContact(siteConfig?.phone || "") || DEFAULT_PHONE;
  const whatsapp = decodeContact(siteConfig?.whatsapp || "") || DEFAULT_WHATSAPP;
  const email = decodeContact(siteConfig?.email || "") || DEFAULT_EMAIL;

  // No `mailto:`/`tel:` in the markup: links are built on click so scrapers
  // reading the HTML find nothing. WhatsApp stays a public wa.me link.
  const channels = [
    {
      id: "phone",
      label: t("phone"),
      icon: <PhoneIcon />,
      onClick: () => {
        window.location.href = `tel:${toTelHref(phone)}`;
      },
    },
    {
      id: "whatsapp",
      label: t("whatsapp"),
      icon: <WhatsAppIcon />,
      href: `https://wa.me/${toWhatsAppNumber(whatsapp)}`,
      target: "_blank",
    },
    {
      id: "email",
      label: t("email"),
      icon: <EmailIcon />,
      onClick: () => {
        window.location.href = `mailto:${email}`;
      },
    },
  ];

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={styles.fab}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t("contact")}
        aria-expanded={isOpen}
        type="button"
      >
        <ChatBubbleIcon />
      </button>

      {isOpen && (
        <div className={styles.popup}>
          {channels.map((ch) => (
            <a
              key={ch.id}
              href={ch.href ?? "#"}
              target={ch.target}
              rel={ch.target === "_blank" ? "noopener noreferrer" : undefined}
              className={styles.channel}
              onClick={(e) => {
                if (ch.onClick) {
                  e.preventDefault();
                  ch.onClick();
                }
                setIsOpen(false);
              }}
            >
              <span className={styles.channelIcon}>{ch.icon}</span>
              <span className={styles.channelLabel}>{ch.label}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactFabButton;
